/** @file
 * @brief A draggable message box component for displaying messages and receiving user responses.
 * @details This component renders a dialog with a message and buttons based on the specified type(ok, yes/no, ok/cancel, etc.).
 * The dialog can be dismissed by clicking the buttons, and it supports an optional timeout after which a default action is taken.
 * The component uses Material-UI for styling and layout, and react-draggable for making the dialog draggable.
 * Localization of button labels is supported through the initMessageBoxText function, allowing for internationalization.
 */

// vendor libraries:
import React from 'react';
import Draggable from 'react-draggable';

import { Paper, PaperProps } from '@mui/material'; //from '@material-ui/core/Paper';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline'
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { ThemeProvider } from '@mui/material/styles';

// our stuff:
import { rvLightTheme } from '../common/themes';

let messageBoxText = { // localization
    YES: "?",
    NO: "?",
    OK: "messageBoxText not initialized",
    CANCEL: "?",
};

export function initMessageBoxText(strings: {
    YES: string;
    NO: string;
    OK: string;
    CANCEL: string;
})
{
    messageBoxText = strings
}

export interface MessageBoxDlgTypeButtonsMap {
  'ok':       'ok';             // message box offering only ok button
  'yesno':    'yes' | 'no';     // message box offering yes/no buttons
  'okcancel': 'ok'  | 'cancel'; // message box offering ok/cancel buttons
//'cancel':   'cancel';
  'yes':      'yes';
//'no':       'no';
}

export type MessageBoxTypes = keyof MessageBoxDlgTypeButtonsMap;

function isPossibleButton<T extends MessageBoxTypes>(boxType: T | undefined, x: MessageBoxDlgTypeButtonsMap[MessageBoxTypes]): x is MessageBoxDlgTypeButtonsMap[T] {
  if ( boxType === undefined )
    return x === "ok";
  return boxType.includes(x);
}

export type MessageBoxProps<T extends MessageBoxTypes = "ok"> = {
  header: string;
  message: string;
  type?: T;
  callback?: (btn: MessageBoxDlgTypeButtonsMap[T]) => void;
  defbutton?: MessageBoxDlgTypeButtonsMap[T];
  timeout?: number;
}

function PaperComponent(props: Readonly<PaperProps>): JSX.Element {
    // not work? const nodeRef = React.useRef(null);
    return <Draggable cancel={'[class*="MuiDialogContent-root"]'}
    // not work? nodeRef={nodeRef} // to avoid warning in react StrictMode(see https://github.com/react-grid-layout/react-draggable/blob/v4.4.2/lib/DraggableCore.js#L159-L171)
    >
        <Paper {...props}
            style={{ cursor: "grab" }}
        />
    </Draggable>
}

function MessageBox<T extends MessageBoxTypes = "ok">({ message, type, callback, timeout = 0 }: MessageBoxProps<T>) {
    const timeoutSeconds = (timeout <= 0) ? 0 : Math.ceil(timeout / 1000);
    const [open, setOpen] = React.useState(true);
    const [timeLeft, setTimeLeft] = React.useState(timeoutSeconds);
    const [, setDisableBackdropClick] = React.useState(true);

    let dismissiveActionId: MessageBoxDlgTypeButtonsMap[MessageBoxTypes];
    switch (type) {
        case "yesno": dismissiveActionId = "no"; break;
        case "okcancel": dismissiveActionId = "cancel"; break;
        //  case "cancel":    dismissiveActionId = "cancel";  break;
        case "yes": dismissiveActionId = "yes"; break;
        //  case "no":        dismissiveActionId = "no";      break;
        case "ok":
        default: dismissiveActionId = "ok"; break;
    }

    const handleClose = function (btnId: MessageBoxDlgTypeButtonsMap[MessageBoxTypes]) {
        return function(): void {
            setOpen(false);

            if (typeof callback === "function") {
                if (isPossibleButton(type, btnId)) {
                    callback(btnId);
                }
                else {
                    throw new Error(`[MessageBox] invalid button ("${btnId}") for requested type("${type}")`);
                }
            }
        };
    }

    React.useEffect(function() {
        setOpen(true);
        setDisableBackdropClick(true);
        setTimeLeft(timeoutSeconds);

        // disable backdrop click momentarily to avoid
        // accidentally closing the dialog if double click happens
        const timeoutId = setTimeout(function(): void {
            setDisableBackdropClick(false);
        }, 333);

        return clearTimeout.bind(null, timeoutId);
    }, []);

    React.useEffect(function() {
        if (!open || timeoutSeconds <= 0)
            return undefined;

        if (timeLeft > 0) {
            const timeoutId = setTimeout(function(): void {
                setTimeLeft(timeLeft - 1);
            }, 1000);

            return clearTimeout.bind(null,timeoutId);
        }

        handleClose(dismissiveActionId)();
        return undefined;
    }, [timeLeft]);

    const buttons: React.ReactNode[] = [];

    const addButton = function (btnId: MessageBoxDlgTypeButtonsMap[MessageBoxTypes], btnLabel: string, btnTimeLeft: number = 0): void {
        buttons.push(
            <Button
                key={btnId}
                onClick={handleClose(btnId)}
                color="primary"
            >
                {btnLabel}{btnTimeLeft > 0 ? ` (${btnTimeLeft})` : ""}
            </Button>
        );
    }

    // dismissive action (cancel/no)
    if (type === 'yesno' || type === 'okcancel') {
        const btnId = (type === "yesno") ? "no" : "cancel";
        const btnLabel = (type === "yesno") ? messageBoxText.NO : messageBoxText.CANCEL;
        const btnIsDismissive = dismissiveActionId === btnId;

        addButton(btnId, btnLabel, btnIsDismissive ? timeLeft : 0);
    }

    // affirmitive action (ok/yes)
    {
        const btnId = (type === "yesno" || type === "yes") ? "yes" : "ok";
        const btnLabel = (type === "yesno" || type === "yes") ? messageBoxText.YES : messageBoxText.OK;
        const btnIsDismissive = dismissiveActionId === btnId;

        addButton(btnId, btnLabel, btnIsDismissive ? timeLeft : 0);
    }

    return <ThemeProvider theme={rvLightTheme}>
        <ScopedCssBaseline>
            <Dialog
                open={open}
                onClose={handleClose(dismissiveActionId)}
                PaperComponent={PaperComponent}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    {/*header*/}
                    <div dangerouslySetInnerHTML={{ __html: message }} />
                </DialogTitle>
                {/* <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                          <div dangerouslySetInnerHTML={{ __html: message }} />
                        </DialogContentText>
                      </DialogContent> */}
                <DialogActions id="alert-dialog-buttons">
                    {buttons}
                </DialogActions>
            </Dialog>
        </ScopedCssBaseline>
    </ThemeProvider>
}

export function createMessageBoxComponent<T extends MessageBoxTypes = 'ok'>(header: string, message: string, callback?: (btn: MessageBoxDlgTypeButtonsMap[T]) => void, type?: T, timeout?: number) {
    return <MessageBox header={header} message={message} type={type} callback={callback} timeout={timeout} />;
}

export default MessageBox;